import type { Companion } from '@/types/game';

/**
 * Companions follow the Peter Jackson trilogy.
 * Most unlock at `isRest: true` locations — those are the safe stops where
 * allies join the Fellowship. Bárbol is the only exception: he is bound to
 * Fangorn (combat) since there is no other lore-faithful place for him.
 *
 * Recruit cost rule of thumb: roughly the gold earned from clearing 30–60%
 * of the zone's pool. Portrait sprites live in `/public/companions/{id}.png`.
 */
export const COMPANIONS: readonly Companion[] = [
  {
    id: 'frodo',
    name: 'Frodo',
    title: 'Portador del Anillo',
    baseDps: 1.8,
    unlockAt: 'comarca',
    color: '#5b8abf',
    recruitCost: 0,
    portrait: '/companions/frodo.png',
    portraitFocus: { x: 60, y: 0, scale: 1.7 },
  },
  {
    id: 'sam',
    name: 'Sam',
    title: 'El Valiente',
    baseDps: 2.2,
    unlockAt: 'comarca',
    color: '#6b9a4a',
    recruitCost: 0,
    portrait: '/companions/sam.png',
    portraitFocus: { x: 40, y: 0, scale: 1.7 },
  },
  {
    id: 'merry',
    name: 'Merry',
    title: 'Caballero de Rohan',
    baseDps: 1.6,
    unlockAt: 'comarca',
    color: '#b8963a',
    recruitCost: 80,
    portrait: '/companions/merry.png',
    portraitScale: 0.67,
  },
  {
    id: 'pippin',
    name: 'Pippin',
    title: 'Guardia de la Ciudadela',
    baseDps: 1.6,
    unlockAt: 'comarca',
    color: '#c9843a',
    recruitCost: 80,
    portrait: '/companions/pippin.png',
    portraitScale: 0.75,
  },
  {
    id: 'aragorn',
    name: 'Aragorn',
    title: 'Trancos',
    baseDps: 8,
    unlockAt: 'bree',
    color: '#5a7a5a',
    recruitCost: 250,
    portrait: '/companions/aragorn.png',
    portraitScale: 0.75,
  },
  {
    id: 'gandalf',
    name: 'Gandalf',
    title: 'El Gris',
    baseDps: 9,
    unlockAt: 'bree',
    color: '#9a9aaa',
    recruitCost: 600,
    portrait: '/companions/gandalf.png',
  },
  {
    id: 'arwen',
    name: 'Arwen',
    title: 'Estrella de la Tarde',
    baseDps: 7,
    unlockAt: 'rivendel',
    color: '#c8d8e8',
    recruitCost: 1200,
    portrait: '/companions/arwen.png',
    portraitGlow: 9,
  },
  {
    id: 'legolas',
    name: 'Legolas',
    title: 'Príncipe del Bosque Negro',
    baseDps: 7.5,
    unlockAt: 'rivendel',
    color: '#7ab89a',
    recruitCost: 1300,
    portrait: '/companions/legolas.png',
    portraitGlow: 9,
  },
  {
    id: 'gimli',
    name: 'Gimli',
    title: 'Hijo de Glóin',
    baseDps: 7.8,
    unlockAt: 'rivendel',
    color: '#a86a3a',
    recruitCost: 1300,
    portrait: '/companions/gimli.png',
    portraitScale: 0.82,
  },
  {
    id: 'boromir',
    name: 'Boromir',
    title: 'Capitán de Gondor',
    baseDps: 8.2,
    unlockAt: 'rivendel',
    color: '#8a8a9a',
    recruitCost: 1400,
    portrait: '/companions/boromir.png',
  },
  {
    id: 'galadriel',
    name: 'Galadriel',
    title: 'Dama de Lórien',
    baseDps: 14,
    unlockAt: 'lothlorien',
    color: '#c8b8e0',
    recruitCost: 8000,
    portrait: '/companions/galadriel.png',
    portraitGlow: 24,
  },
  {
    id: 'celeborn',
    name: 'Celeborn',
    title: 'Señor de Lórien',
    baseDps: 12,
    unlockAt: 'lothlorien',
    color: '#c8c8d8',
    recruitCost: 6500,
    portrait: '/companions/celeborn.png',
    portraitScale: 1.2,
    portraitGlow: 24,
  },
  {
    id: 'barbol',
    name: 'Bárbol',
    title: 'Pastor de Árboles',
    baseDps: 10,
    unlockAt: 'fangorn',
    color: '#5a7040',
    recruitCost: 12000,
    portrait: '/companions/barbol.png',
  },
  {
    id: 'theoden',
    name: 'Théoden',
    title: 'Rey de Rohan',
    baseDps: 11,
    unlockAt: 'edoras',
    color: '#c4a040',
    recruitCost: 18000,
    portrait: '/companions/theoden.png',
    portraitScale: 0.8,
  },
  {
    id: 'eomer',
    name: 'Éomer',
    title: 'Mariscal de la Marca',
    baseDps: 13,
    unlockAt: 'edoras',
    color: '#a08830',
    recruitCost: 20000,
    portrait: '/companions/eomer.png',
    portraitScale: 0.9,
  },
  {
    id: 'eowyn',
    name: 'Éowyn',
    title: 'Dama de Rohan',
    baseDps: 14,
    unlockAt: 'edoras',
    color: '#d4b870',
    recruitCost: 22000,
    portrait: '/companions/eowyn.png',
    portraitScale: 1.1,
  },
  {
    id: 'gandalf_blanco',
    name: 'Gandalf',
    title: 'El Blanco',
    baseDps: 16,
    unlockAt: 'edoras',
    color: '#f0f0f0',
    recruitCost: 28000,
    portrait: '/companions/gandalf-white.png',
    portraitScale: 1.35,
  },
  {
    id: 'rey_muertos',
    name: 'Rey de los Muertos',
    title: 'Señor del Juramento Roto',
    baseDps: 18,
    unlockAt: 'paso_de_los_muertos',
    color: '#8ea5b0',
    recruitCost: 35000,
    portrait: '/companions/king-dead.png',
    requireBossDefeated: 'paso_de_los_muertos',
    portraitScale: 1.4,
    portraitGlow: 24,
    portraitGlowColor: '102, 217, 217',
    portraitOffsetY: 12,
  },
  {
    id: 'faramir',
    name: 'Faramir',
    title: 'Capitán de Ithilien',
    baseDps: 12,
    unlockAt: 'ithilien',
    color: '#7080a0',
    recruitCost: 45000,
    portrait: '/companions/faramir.png',
    portraitScale: 0.75,
  },
  {
    id: 'rey_aragorn',
    name: 'Rey Aragorn',
    title: 'Rey de Gondor',
    baseDps: 15,
    unlockAt: 'minas_tirith',
    color: '#5a7a5a',
    recruitCost: 90000,
    portrait: '/companions/king-aragorn.png',
    portraitScale: 0.75,
  },
];
